import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCurrencyMaster } from "../../hooks/useCurrencyMaster";
import { useFactoryDetailsMaster } from "../../hooks/useFactoryDetailsMaster";
import { CURRENCIES } from "../website/contractManufacturerRfqFormState";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCloudUploadAlt,
  FaDownload,
  FaEye,
  FaFileAlt,
  FaPaperPlane,
  FaPlus,
  FaSave,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/buyer_contract_manufacturer_agreement.scss";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";

function toNumber(v) {
  if (v == null || v === "") return NaN;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

function formatCurrency(amount, currency = "USD") {
  const n = toNumber(amount);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency || "USD"} ${n}`;
  }
}

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function getObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

/** CommonService returns axios body `{ success, message, data }` — use inner `data` for RFQ/agreement payloads. */
function unwrapApiEnvelope(raw) {
  if (raw != null && typeof raw === "object" && raw.data != null && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    return raw.data;
  }
  return raw;
}

function agreementStatusLower(agreement) {
  if (!agreement || typeof agreement !== "object") return "";
  const s = agreement.status ?? agreement.agreement_status;
  return String(s ?? "").trim().toLowerCase();
}

/** HTML date inputs require YYYY-MM-DD; normalize API / ISO strings so the picker works. */
function toIsoDateOnly(v) {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const prefix = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (prefix) return prefix[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

/** Build a single-line registered address from company profile JSON. */
function formatRegisteredAddress(company) {
  const c = getObj(company);
  const primary = getObj(c.address);
  const overseas = getObj(c.overseas_address);
  const addr = Object.keys(primary).length ? primary : overseas;
  if (!addr || Object.keys(addr).length === 0) return "";
  const parts = [
    addr.address_line1 || addr.line1 || addr.line_1,
    addr.address_line2 || addr.line2,
    addr.city_name || addr.cityName,
    addr.state_name || addr.stateName,
    addr.country_name || addr.countryName,
    addr.postal_code || addr.postalCode,
  ].filter(Boolean);
  return parts.join(", ");
}

/** Map buyer/CM company profile (agreement context API) to party fields. */
function buildPartyFromCompanyProfile(company) {
  const c = getObj(company);
  const cd = getObj(c.contact_details);
  return {
    companyName: String(c.company_name || "").trim(),
    authorizedRepresentative: String(cd.contact_name || "").trim(),
    designation: String(c.designation || "").trim(),
    email: String(cd.contact_email || c.user_email || "").trim(),
    registeredAddress: formatRegisteredAddress(c),
    contactNumber: String(cd.contact_number || c.user_phone || "").trim(),
  };
}

const CONTRACTING_PARTY_BLOCKS = [
  { key: "buyer", heading: "Buyer (First Party)" },
  { key: "manufacturer", heading: "Manufacturer (Second Party)" },
];

function extractApiErrorMessage(err) {
  return err?.response?.data?.message || err?.message || "Request failed";
}

/** Match stored form values to master option values (numeric IDs from API vs string). */
function masterValuesMatch(stored, optionValue) {
  if (stored === optionValue) return true;
  if (stored === "" || stored === undefined || stored === null) {
    return optionValue === "" || optionValue === undefined || optionValue === null;
  }
  return String(stored) === String(optionValue);
}

/** Resolve display label for a stored value using the same { value, label } lists as RFQ / quotes. */
function resolveOptionLabel(rawValue, options = []) {
  if (rawValue === "" || rawValue === undefined || rawValue === null) return "";
  const byValue = options.find((opt) => masterValuesMatch(rawValue, opt.value));
  if (byValue?.label) return String(byValue.label).trim();
  const raw = String(rawValue).trim();
  if (!raw) return "";
  const byLabel = options.find((opt) => String(opt?.label ?? "").trim().toLowerCase() === raw.toLowerCase());
  if (byLabel?.label) return String(byLabel.label).trim();
  return raw;
}

function coerceMasterSelectValue(raw) {
  if (raw === "") return "";
  const n = Number(raw);
  if (Number.isFinite(n) && String(n) === String(raw).trim()) return n;
  return raw;
}

function dash(v) {
  const s = v == null ? "" : String(v).trim();
  return s || "—";
}

function optLabel(value, options) {
  return resolveOptionLabel(value, options) || "—";
}

const IP_OWNERSHIP_OPTIONS = [
  { value: "", label: "Select ownership" },
  { value: "buyer_retains_all", label: "Buyer retains all IP rights" },
  { value: "manufacturer_retains_all", label: "Manufacturer retains all IP rights" },
  { value: "shared_ownership", label: "Shared ownership" },
  { value: "custom_arrangement", label: "Custom arrangement" },
];

/** Map older saved agreement values to current IP ownership option values. */
function mapLegacyIpOwnership(stored) {
  const v = String(stored ?? "").trim();
  if (!v) return "";
  const legacy = {
    buyer: "buyer_retains_all",
    manufacturer: "manufacturer_retains_all",
    joint: "shared_ownership",
    work_for_hire: "custom_arrangement",
  };
  return legacy[v] || v;
}

const AGREEMENT_TYPE_OPTIONS = [
  { value: "", label: "Select agreement type" },
  { value: "purchase_agreement", label: "Purchase Agreement" },
  { value: "service_agreement", label: "Service Agreement" },
  { value: "non_disclosure_agreement", label: "Non-Disclosure Agreement" },
  { value: "master_service_agreement", label: "Master Service Agreement" },
];

const GOVERNING_LAW_OPTIONS = [
  { value: "", label: "Select jurisdiction" },
  { value: "in", label: "Indian Law" },
  { value: "us", label: "US Law" },
  { value: "uk", label: "UK Law" },
  { value: "sg", label: "Singapore Law" },
];

const TERMINATION_NOTICE_OPTIONS = [
  { value: "", label: "Select period" },
  { value: "15", label: "15 Days" },
  { value: "30", label: "30 Days" },
  { value: "60", label: "60 Days" },
  { value: "90", label: "90 Days" },
];

const DISPUTE_RESOLUTION_OPTIONS = [
  { value: "", label: "Select method" },
  { value: "arbitration", label: "Arbitration" },
  { value: "mediation", label: "Mediation" },
  { value: "litigation", label: "Litigation" },
];

function newLocalId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function BuyerContractManufacturerAgreementCreatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rfqId = Number(searchParams.get("rfqId"));
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const annexureFileInputRef = useRef(null);

  const [form, setForm] = useState({
    agreementInfo: {
      agreementNumber: "",
      title: "Manufacturing Service Agreement",
      agreementType: "",
      governingLaw: "",
      effectiveDate: "",
      validUntil: "",
      currency: "USD",
    },
    parties: {
      buyer: {
        companyName: "",
        authorizedRepresentative: "",
        designation: "",
        email: "",
        registeredAddress: "",
        contactNumber: "",
      },
      manufacturer: {
        companyName: "",
        authorizedRepresentative: "",
        designation: "",
        email: "",
        registeredAddress: "",
        contactNumber: "",
      },
    },
    commercial: {
      paymentTerms: "",
      paymentMethod: "",
      incoterms: "",
      lateDeliveryPenalty: "",
      warrantyPeriod: "",
      securityDeposit: "",
      deliveryDate: "",
      dispatchLeadTimeDays: "",
      portOfShipping: "",
    },
    pricing: {
      unitPrice: "",
      taxPercent: "",
      taxAmount: "",
      totalAmount: "",
      notes: "",
    },
    agreementClauses: {
      scopeOfWork: "",
      qualityStandards: "",
      deliveryTerms: "",
    },
    paymentMilestones: [{ id: "pm-initial", name: "", deliverable: "", dueDate: "", paymentPercent: "" }],
    customClauses: [],
    legal: {
      terminationNoticePeriod: "",
      disputeResolution: "",
      terminationConditions: "",
    },
    confidentiality: {
      ipOwnership: "",
      ndaApplies: false,
      technicalDataConfidential: false,
      marketingUseRestricted: false,
    },
    additional: {
      scope: "",
      warranty: "",
      notes: "",
    },
  });

  const { shippingTermsOptions, paymentScheduleOptions } = useFactoryDetailsMaster();
  const { currencyOptions } = useCurrencyMaster();

  /** Same source as ContractManufacturerRfqPage: /master/currencies → currencyOptions; fallback CURRENCIES. */
  const currencySelectOptions = useMemo(() => {
    if (currencyOptions?.length) {
      const mapped = currencyOptions
        .map((o) => {
          const code = String(o.currencyCode || o.currency_code || o.code || "").trim();
          const value = code || String(o.value ?? "");
          return { value, label: o.label || value };
        })
        .filter((o) => o.value !== "");
      return [{ value: "", label: "Select currency" }, ...mapped];
    }
    return [{ value: "", label: "Select currency" }, ...CURRENCIES.map((c) => ({ value: c, label: c }))];
  }, [currencyOptions]);

  /** Same as ContractManufacturerRfqPage: paymentScheduleOptions from /master/factory-ShippingTerms (paymentSchedule). */
  const paymentTermsSelectOptions = useMemo(() => {
    const base = paymentScheduleOptions?.length
      ? paymentScheduleOptions
      : [{ value: "", label: "Payment Terms" }];
    if (base.length && String(base[0]?.value ?? "") !== "") {
      return [{ value: "", label: "Select" }, ...base];
    }
    return base;
  }, [paymentScheduleOptions]);

  /** Same as ContractManufacturerRfqPage: shippingTermsOptions (incoterms from API). */
  const incotermsSelectOptions = useMemo(() => {
    const base = shippingTermsOptions?.length
      ? shippingTermsOptions
      : [{ value: "", label: "Incoterms" }];
    if (base.length && String(base[0]?.value ?? "") !== "") {
      return [{ value: "", label: "Select" }, ...base];
    }
    return base;
  }, [shippingTermsOptions]);

  const summary = useMemo(() => {
    const rfq = ctx?.rfq || {};
    const quote = ctx?.quote || null;
    const quotePayload = getObj(quote?.quote_payload_json);
    const base = getObj(quotePayload?.basePrice);
    const currency = String(base?.currency || form?.agreementInfo?.currency || "USD");
    const total = form?.pricing?.totalAmount || base?.price || "";
    const lead = getObj(quotePayload?.sample)?.dispatchLeadTimeDays || form?.commercial?.dispatchLeadTimeDays || "";
    const commercial = getObj(quotePayload?.commercial);
    const ptRaw = commercial?.paymentTerms ?? form?.commercial?.paymentTerms;
    const paymentTermsLabel = resolveOptionLabel(ptRaw, paymentTermsSelectOptions) || "—";
    return {
      rfqNo: rfq?.id ? `RFQ-${String(rfq.id).padStart(5, "0")}` : "—",
      qtNo: quote?.id ? `QT-${String(quote.id).padStart(6, "0")}` : "—",
      totalText: total === "" ? "—" : formatCurrency(total, currency),
      leadTime: lead ? `${lead} days` : "—",
      paymentTerms: paymentTermsLabel,
    };
  }, [ctx, form, paymentTermsSelectOptions]);

  /** After send for signature (or signed/cancelled), buyer can only view — no draft/submit actions. */
  const isAgreementLocked = useMemo(() => {
    const s = agreementStatusLower(ctx?.agreement);
    return s === "submitted" || s === "signed" || s === "cancelled";
  }, [ctx?.agreement]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        if (!Number.isFinite(rfqId) || rfqId <= 0) {
          setLoading(false);
          setCtx(null);
          return;
        }
        setLoading(true);
        const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.AGREEMENT_CONTEXT(rfqId));
        const payload = unwrapApiEnvelope(res);
        if (cancelled) return;
        setCtx(payload);

        const rfq = payload?.rfq || {};
        const quote = payload?.quote || null;
        const quotePayload = getObj(quote?.quote_payload_json);
        const rfqPayload = getObj(rfq?.payload_json);
        const project = getObj(rfqPayload?.project);
        const business = getObj(rfqPayload?.business);
        const quoteCommercial = getObj(quotePayload?.commercial);
        const quoteSample = getObj(quotePayload?.sample);
        const basePrice = getObj(quotePayload?.basePrice);
        const sellerCompany = payload?.sellerCompany || {};
        const buyerCompany = payload?.buyerCompany || {};

        const currency = String(basePrice?.currency || buyerCompany?.currency || "USD");
        const unitPrice = basePrice?.price ?? "";
        const priceValidityDays = quoteCommercial?.priceValidityDays ?? quoteCommercial?.priceValidity ?? "";

        const now = new Date();
        const effectiveDate = now.toISOString().slice(0, 10);
        const validUntil =
          priceValidityDays && Number.isFinite(toNumber(priceValidityDays))
            ? (() => {
                const d = new Date(now);
                d.setDate(d.getDate() + toNumber(priceValidityDays));
                return d.toISOString().slice(0, 10);
              })()
            : "";

        const existingAgreementPayload = getObj(payload?.agreement?.agreement_payload_json);
        const rfqYear = rfq?.created_at ? new Date(rfq.created_at).getFullYear() : new Date().getFullYear();
        const autoAgreementNo = `AGR-${rfqYear}-${String(rfq?.id ?? rfqId ?? 0).padStart(3, "0")}`;

        setForm((prev) => ({
          ...prev,
          ...existingAgreementPayload,
          agreementInfo: {
            ...prev.agreementInfo,
            ...getObj(existingAgreementPayload.agreementInfo),
            agreementNumber:
              getObj(existingAgreementPayload.agreementInfo).agreementNumber ||
              autoAgreementNo,
            title:
              getObj(existingAgreementPayload.agreementInfo).title ||
              "Manufacturing Service Agreement",
            agreementType: getObj(existingAgreementPayload.agreementInfo).agreementType || "",
            governingLaw: getObj(existingAgreementPayload.agreementInfo).governingLaw || "",
            effectiveDate: toIsoDateOnly(getObj(existingAgreementPayload.agreementInfo).effectiveDate) || effectiveDate,
            validUntil: toIsoDateOnly(getObj(existingAgreementPayload.agreementInfo).validUntil) || validUntil,
            currency: getObj(existingAgreementPayload.agreementInfo).currency || currency,
          },
          parties: (() => {
            const ep = getObj(existingAgreementPayload.parties);
            const eb = getObj(ep.buyer);
            const em = getObj(ep.manufacturer);
            const buyerAuto = buildPartyFromCompanyProfile(buyerCompany);
            const mfrAuto = buildPartyFromCompanyProfile(sellerCompany);
            return {
              buyer: {
                companyName:
                  eb.companyName ||
                  ep.buyerName ||
                  business?.companyName ||
                  buyerAuto.companyName ||
                  buyerCompany?.company_name ||
                  "",
                authorizedRepresentative:
                  eb.authorizedRepresentative ||
                  business?.contactName ||
                  buyerAuto.authorizedRepresentative ||
                  "",
                designation: eb.designation || buyerAuto.designation || "",
                email:
                  eb.email ||
                  ep.buyerEmail ||
                  business?.email ||
                  buyerAuto.email ||
                  rfq?.contact_email ||
                  buyerCompany?.user_email ||
                  "",
                registeredAddress: eb.registeredAddress || buyerAuto.registeredAddress || formatRegisteredAddress(buyerCompany) || "",
                contactNumber:
                  eb.contactNumber ||
                  ep.buyerPhone ||
                  business?.phone ||
                  buyerAuto.contactNumber ||
                  buyerCompany?.user_phone ||
                  "",
              },
              manufacturer: {
                companyName: em.companyName || ep.sellerName || mfrAuto.companyName || sellerCompany?.company_name || "",
                authorizedRepresentative: em.authorizedRepresentative || mfrAuto.authorizedRepresentative || "",
                designation: em.designation || mfrAuto.designation || "",
                email:
                  em.email ||
                  ep.sellerEmail ||
                  mfrAuto.email ||
                  getObj(sellerCompany?.contact_details)?.contact_email ||
                  "",
                registeredAddress: em.registeredAddress || mfrAuto.registeredAddress || formatRegisteredAddress(sellerCompany) || "",
                contactNumber:
                  em.contactNumber || ep.sellerPhone || mfrAuto.contactNumber || getObj(sellerCompany?.contact_details)?.contact_number || "",
              },
            };
          })(),
          commercial: {
            ...prev.commercial,
            ...getObj(existingAgreementPayload.commercial),
            paymentTerms: getObj(existingAgreementPayload.commercial).paymentTerms || quoteCommercial?.paymentTerms || "",
            paymentMethod: getObj(existingAgreementPayload.commercial).paymentMethod || quoteCommercial?.paymentMethod || "",
            incoterms: getObj(existingAgreementPayload.commercial).incoterms || quoteCommercial?.incoterms || "",
            lateDeliveryPenalty: getObj(existingAgreementPayload.commercial).lateDeliveryPenalty || "",
            warrantyPeriod: getObj(existingAgreementPayload.commercial).warrantyPeriod || "",
            securityDeposit: getObj(existingAgreementPayload.commercial).securityDeposit || "",
            deliveryDate:
              getObj(existingAgreementPayload.commercial).deliveryDate ||
              (quoteSample?.dispatchLeadTimeDays ? "" : "") ||
              project?.expectedStartDate ||
              "",
            dispatchLeadTimeDays:
              getObj(existingAgreementPayload.commercial).dispatchLeadTimeDays ||
              quoteSample?.dispatchLeadTimeDays ||
              "",
            portOfShipping: getObj(existingAgreementPayload.commercial).portOfShipping || quoteCommercial?.portOfShipping || "",
          },
          agreementClauses: (() => {
            const ac = getObj(existingAgreementPayload.agreementClauses);
            const addl = getObj(existingAgreementPayload.additional);
            return {
              scopeOfWork: ac.scopeOfWork || addl.scope || project?.title || "",
              qualityStandards: ac.qualityStandards || "",
              deliveryTerms: ac.deliveryTerms || "",
            };
          })(),
          paymentMilestones: (() => {
            const raw = existingAgreementPayload.paymentMilestones;
            if (Array.isArray(raw) && raw.length > 0) {
              return raw.map((m, i) => ({
                id: m.id || `pm-${i}`,
                name: m.name ?? "",
                deliverable: m.deliverable ?? "",
                dueDate: toIsoDateOnly(m.dueDate),
                paymentPercent: m.paymentPercent ?? "",
              }));
            }
            return [{ id: "pm-initial", name: "", deliverable: "", dueDate: "", paymentPercent: "" }];
          })(),
          customClauses: (() => {
            const raw = existingAgreementPayload.customClauses;
            if (!Array.isArray(raw) || raw.length === 0) return [];
            return raw.map((c, i) => ({
              id: c.id || `cc-${i}`,
              title: c.title ?? "",
              content: c.content ?? "",
            }));
          })(),
          legal: {
            ...getObj(prev.legal),
            ...getObj(existingAgreementPayload.legal),
            terminationNoticePeriod: getObj(existingAgreementPayload.legal).terminationNoticePeriod || "",
            disputeResolution: getObj(existingAgreementPayload.legal).disputeResolution || "",
            terminationConditions: getObj(existingAgreementPayload.legal).terminationConditions || "",
          },
          confidentiality: (() => {
            const raw = existingAgreementPayload.confidentiality;
            if (raw && typeof raw === "object" && !Array.isArray(raw)) {
              return {
                ipOwnership: mapLegacyIpOwnership(raw.ipOwnership || ""),
                ndaApplies: !!raw.ndaApplies,
                technicalDataConfidential: !!raw.technicalDataConfidential,
                marketingUseRestricted: !!raw.marketingUseRestricted,
              };
            }
            return {
              ipOwnership: "",
              ndaApplies: false,
              technicalDataConfidential: false,
              marketingUseRestricted: false,
            };
          })(),
          pricing: {
            ...prev.pricing,
            ...getObj(existingAgreementPayload.pricing),
            unitPrice: getObj(existingAgreementPayload.pricing).unitPrice || unitPrice,
            totalAmount:
              getObj(existingAgreementPayload.pricing).totalAmount ||
              (unitPrice !== "" ? unitPrice : ""),
          },
          additional: {
            ...prev.additional,
            ...getObj(existingAgreementPayload.additional),
            scope: getObj(existingAgreementPayload.additional).scope || project?.title || "RFQ project scope",
            warranty: getObj(existingAgreementPayload.additional).warranty || quoteSample?.warranty || "",
            notes: getObj(existingAgreementPayload.additional).notes || "",
          },
        }));
      } catch (e) {
        if (!cancelled) {
          toast.error(extractApiErrorMessage(e));
          setCtx(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [rfqId]);

  useEffect(() => {
    if (!previewOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("cm-agreement-preview-open");
    function onKey(e) {
      if (e.key === "Escape") setPreviewOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove("cm-agreement-preview-open");
      window.removeEventListener("keydown", onKey);
    };
  }, [previewOpen]);

  function setPath(path, value) {
    setForm((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i += 1) {
        const k = parts[i];
        cur[k] = { ...(cur[k] || {}) };
        cur = cur[k];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }

  function addMilestone() {
    setForm((prev) => ({
      ...prev,
      paymentMilestones: [
        ...(prev.paymentMilestones || []),
        { id: newLocalId("pm"), name: "", deliverable: "", dueDate: "", paymentPercent: "" },
      ],
    }));
  }

  function removeMilestone(index) {
    setForm((prev) => {
      const list = [...(prev.paymentMilestones || [])];
      list.splice(index, 1);
      const nextList =
        list.length > 0 ? list : [{ id: newLocalId("pm"), name: "", deliverable: "", dueDate: "", paymentPercent: "" }];
      return { ...prev, paymentMilestones: nextList };
    });
  }

  function updateMilestone(index, field, value) {
    setForm((prev) => {
      const list = [...(prev.paymentMilestones || [])];
      const row = { ...list[index], [field]: value };
      list[index] = row;
      return { ...prev, paymentMilestones: list };
    });
  }

  function addCustomClause() {
    setForm((prev) => ({
      ...prev,
      customClauses: [...(prev.customClauses || []), { id: newLocalId("cc"), title: "", content: "" }],
    }));
  }

  function removeCustomClause(index) {
    setForm((prev) => {
      const list = [...(prev.customClauses || [])];
      list.splice(index, 1);
      return { ...prev, customClauses: list };
    });
  }

  function updateCustomClause(index, field, value) {
    setForm((prev) => {
      const list = [...(prev.customClauses || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, customClauses: list };
    });
  }

  function validatePartyBranch(prefix, party) {
    const errors = {};
    const p = getObj(party);
    if (!String(p.companyName || "").trim()) {
      errors[`${prefix}.companyName`] = "Company name is required";
    }
    if (!String(p.authorizedRepresentative || "").trim()) {
      errors[`${prefix}.authorizedRepresentative`] = "Authorized representative is required";
    }
    if (!String(p.designation || "").trim()) {
      errors[`${prefix}.designation`] = "Designation is required";
    }
    if (!String(p.email || "").trim()) {
      errors[`${prefix}.email`] = "Email is required";
    }
    if (!String(p.registeredAddress || "").trim()) {
      errors[`${prefix}.registeredAddress`] = "Registered address is required";
    }
    if (!String(p.contactNumber || "").trim()) {
      errors[`${prefix}.contactNumber`] = "Contact number is required";
    }
    return errors;
  }

  function validate() {
    const errors = {};
    if (!String(form?.agreementInfo?.title || "").trim()) errors["agreementInfo.title"] = "Agreement title is required";
    if (!String(form?.agreementInfo?.effectiveDate || "").trim()) {
      errors["agreementInfo.effectiveDate"] = "Agreement start date is required";
    }
    Object.assign(errors, validatePartyBranch("parties.buyer", form?.parties?.buyer));
    Object.assign(errors, validatePartyBranch("parties.manufacturer", form?.parties?.manufacturer));
    if (!String(form?.pricing?.totalAmount || "").trim()) errors["pricing.totalAmount"] = "Total contract value is required";
    if (!String(form?.agreementInfo?.agreementType || "").trim()) {
      errors["agreementInfo.agreementType"] = "Agreement type is required";
    }
    if (!String(form?.agreementInfo?.governingLaw || "").trim()) {
      errors["agreementInfo.governingLaw"] = "Governing law / jurisdiction is required";
    }
    if (!String(form?.agreementInfo?.validUntil || "").trim()) {
      errors["agreementInfo.validUntil"] = "Agreement end date is required";
    }
    if (!String(form?.agreementInfo?.currency || "").trim()) {
      errors["agreementInfo.currency"] = "Currency is required";
    }
    if (!String(form?.commercial?.paymentTerms || "").trim()) {
      errors["commercial.paymentTerms"] = "Payment terms are required";
    }
    if (!String(form?.commercial?.incoterms || "").trim()) {
      errors["commercial.incoterms"] = "Incoterms are required";
    }
    if (!String(form?.commercial?.lateDeliveryPenalty || "").trim()) {
      errors["commercial.lateDeliveryPenalty"] = "Late delivery penalty is required";
    }
    if (!String(form?.commercial?.warrantyPeriod || "").trim()) {
      errors["commercial.warrantyPeriod"] = "Warranty period is required";
    }
    if (!String(form?.agreementClauses?.scopeOfWork || "").trim()) {
      errors["agreementClauses.scopeOfWork"] = "Scope of work is required";
    }
    if (!String(form?.agreementClauses?.qualityStandards || "").trim()) {
      errors["agreementClauses.qualityStandards"] = "Quality standards are required";
    }
    if (!String(form?.agreementClauses?.deliveryTerms || "").trim()) {
      errors["agreementClauses.deliveryTerms"] = "Delivery terms are required";
    }
    if (!String(form?.legal?.terminationNoticePeriod || "").trim()) {
      errors["legal.terminationNoticePeriod"] = "Termination notice period is required";
    }
    if (!String(form?.legal?.disputeResolution || "").trim()) {
      errors["legal.disputeResolution"] = "Dispute resolution method is required";
    }
    if (!String(form?.confidentiality?.ipOwnership || "").trim()) {
      errors["confidentiality.ipOwnership"] = "IP ownership is required";
    }
    (form.paymentMilestones || []).forEach((m, i) => {
      if (!String(m.name || "").trim()) errors[`paymentMilestones.${i}.name`] = "Milestone name is required";
      if (!String(m.deliverable || "").trim()) errors[`paymentMilestones.${i}.deliverable`] = "Deliverable is required";
      if (!String(m.dueDate || "").trim()) errors[`paymentMilestones.${i}.dueDate`] = "Due date is required";
      const pct = toNumber(m.paymentPercent);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        errors[`paymentMilestones.${i}.paymentPercent`] = "Enter a percentage between 0 and 100";
      }
    });
    (form.customClauses || []).forEach((c, i) => {
      const hasAny = String(c.title || "").trim() || String(c.content || "").trim();
      if (hasAny) {
        if (!String(c.title || "").trim()) errors[`customClauses.${i}.title`] = "Clause title is required";
        if (!String(c.content || "").trim()) errors[`customClauses.${i}.content`] = "Clause content is required";
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function save(status = "draft") {
    if (isAgreementLocked) {
      toast.info("This agreement has already been sent for signature and cannot be changed here.");
      return;
    }
    try {
      setSaving(true);
      const ok = validate();
      if (!ok) {
        toast.error("Please fix the highlighted fields");
        return;
      }
      const { confidentialityIp: _legacyIp, ...formWithoutLegacyIp } = form;
      const payload = {
        ...formWithoutLegacyIp,
        attachments: attachments.map((f) => ({ name: f.name, type: f.type, size: f.size })),
      };
      const res = await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SAVE_AGREEMENT(rfqId), { payload, status });
      const saved = unwrapApiEnvelope(res);
      if (saved && typeof saved === "object" && (saved.status != null || saved.id != null)) {
        setCtx((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            agreement: {
              ...(prev.agreement || {}),
              ...(saved.id != null ? { id: saved.id } : {}),
              ...(saved.status != null ? { status: saved.status } : {}),
            },
          };
        });
      }
      setFieldErrors({});
      toast.success(
        res?.message ||
          (status === "submitted" ? "Agreement sent for signature" : "Agreement saved as draft"),
      );
      const cmCompanyId = ctx?.rfq?.company_id;
      if (cmCompanyId != null && Number(cmCompanyId) > 0) {
        navigate(`/dashboard/buyer/contract-manufacturers/quotes?companyId=${Number(cmCompanyId)}`);
      } else {
        navigate("/dashboard/buyer/contract-manufacturers/quotes");
      }
    } catch (e) {
      const fe = e?.response?.data?.fieldErrors;
      if (fe && typeof fe === "object") setFieldErrors(fe);
      toast.error(extractApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  function previewAgreement() {
    setPreviewOpen(true);
  }

  function closePreview() {
    setPreviewOpen(false);
  }

  function printPreview() {
    document.body.classList.add("cm-agreement-preview-mode");
    function onAfterPrint() {
      document.body.classList.remove("cm-agreement-preview-mode");
      window.removeEventListener("afterprint", onAfterPrint);
    }
    window.addEventListener("afterprint", onAfterPrint);
    setTimeout(() => window.print(), 100);
  }

  function exportAgreementPdf() {
    if (previewOpen) {
      printPreview();
      return;
    }
    toast.info("Use the print dialog and choose “Save as PDF” to export.");
    setTimeout(() => window.print(), 200);
  }

  if (loading) {
    return (
      <div className="cm-agreement-page">
        <div className="cm-agreement-wrap">
          <div className="cm-agreement-card">
            <strong>Loading agreement…</strong>
          </div>
        </div>
      </div>
    );
  }

  if (!ctx) {
    return (
      <div className="cm-agreement-page">
        <div className="cm-agreement-wrap">
          <div className="cm-agreement-card">
            <strong>Agreement context not available.</strong>
            <div className="mt-2">
              <button type="button" className="cm-agreement-btn-secondary" onClick={() => navigate(-1)}>
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cm-agreement-page">
      <div className="cm-agreement-wrap">
        <div className="cm-agreement-header">
          <div>
            <h1 className="cm-agreement-title">
              {isAgreementLocked ? "Purchase Agreement" : "Create Purchase Agreement"}
            </h1>
            <p className="cm-agreement-sub">
              {isAgreementLocked
                ? "Sent for signature — view details below. Editing and re-sending are disabled."
                : "Review and finalize an agreement based on the accepted quotation."}
            </p>
          </div>
          <div className="cm-agreement-top-actions">
            <button type="button" className="cm-agreement-btn-secondary" onClick={() => navigate(-1)} disabled={saving}>
              Back
            </button>
          </div>
        </div>

        <div className="cm-agreement-summary">
          <div className="kv">
            <label>Selected quotation</label>
            <strong>{summary.qtNo}</strong>
          </div>
          <div className="kv">
            <label>RFQ</label>
            <strong>{summary.rfqNo}</strong>
          </div>
          <div className="kv">
            <label>Total value</label>
            <strong>{summary.totalText}</strong>
          </div>
          <div className="kv">
            <label>Lead time</label>
            <strong>{summary.leadTime}</strong>
          </div>
        </div>

        <div className="cm-agreement-card cm-agreement-card--basic">
          <div className="cm-agreement-section-head">
            <span className="cm-agreement-section-icon" aria-hidden>
              <FaFileAlt />
            </span>
            <h3 className="cm-agreement-section-title">Agreement Basic Information</h3>
          </div>

          <div className="cm-agreement-grid-4 cm-agreement-basic-row-1">
            <div className="cm-agreement-field">
              <label>
                Agreement Number<span className="cm-agreement-req">*</span>
              </label>
              <input
                className="cm-agreement-input-readonly"
                value={form.agreementInfo.agreementNumber}
                readOnly
                tabIndex={-1}
              />
            </div>
            <div className="cm-agreement-field">
              <label>
                Agreement Title<span className="cm-agreement-req">*</span>
              </label>
              <input
                value={form.agreementInfo.title}
                onChange={(e) => setPath("agreementInfo.title", e.target.value)}
                aria-invalid={!!fieldErrors["agreementInfo.title"]}
              />
              {fieldErrors["agreementInfo.title"] && <div className="cm-agreement-error">{fieldErrors["agreementInfo.title"]}</div>}
            </div>
            <div className="cm-agreement-field">
              <label>
                Agreement Type<span className="cm-agreement-req">*</span>
              </label>
              <select
                className="cm-agreement-select-native"
                value={form.agreementInfo.agreementType}
                onChange={(e) => {
                  setPath("agreementInfo.agreementType", e.target.value);
                  if (fieldErrors["agreementInfo.agreementType"]) {
                    setFieldErrors((prev) => ({ ...prev, "agreementInfo.agreementType": undefined }));
                  }
                }}
                aria-invalid={!!fieldErrors["agreementInfo.agreementType"]}
              >
                {AGREEMENT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value || "at"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {fieldErrors["agreementInfo.agreementType"] && (
                <div className="cm-agreement-error">{fieldErrors["agreementInfo.agreementType"]}</div>
              )}
            </div>
            <div className="cm-agreement-field">
              <label>
                Governing Law / Jurisdiction<span className="cm-agreement-req">*</span>
              </label>
              <select
                className="cm-agreement-select-native"
                value={form.agreementInfo.governingLaw}
                onChange={(e) => {
                  setPath("agreementInfo.governingLaw", e.target.value);
                  if (fieldErrors["agreementInfo.governingLaw"]) {
                    setFieldErrors((prev) => ({ ...prev, "agreementInfo.governingLaw": undefined }));
                  }
                }}
                aria-invalid={!!fieldErrors["agreementInfo.governingLaw"]}
              >
                {GOVERNING_LAW_OPTIONS.map((o) => (
                  <option key={o.value || "gl"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {fieldErrors["agreementInfo.governingLaw"] && (
                <div className="cm-agreement-error">{fieldErrors["agreementInfo.governingLaw"]}</div>
              )}
            </div>
          </div>

          <div className="cm-agreement-basic-row-2">
            <div className="cm-agreement-field">
              <label>
                Agreement Start Date<span className="cm-agreement-req">*</span>
              </label>
              <div className="cm-agreement-date-field">
                <FaCalendarAlt className="cm-agreement-date-icon" aria-hidden />
                <input
                  type="date"
                  className="cm-agreement-date-input"
                  value={form.agreementInfo.effectiveDate}
                  onChange={(e) => setPath("agreementInfo.effectiveDate", e.target.value)}
                  aria-invalid={!!fieldErrors["agreementInfo.effectiveDate"]}
                />
              </div>
              {fieldErrors["agreementInfo.effectiveDate"] && (
                <div className="cm-agreement-error">{fieldErrors["agreementInfo.effectiveDate"]}</div>
              )}
            </div>
            <div className="cm-agreement-field">
              <label>
                Agreement End Date<span className="cm-agreement-req">*</span>
              </label>
              <div className="cm-agreement-date-field">
                <FaCalendarAlt className="cm-agreement-date-icon" aria-hidden />
                <input
                  type="date"
                  className="cm-agreement-date-input"
                  value={form.agreementInfo.validUntil}
                  onChange={(e) => setPath("agreementInfo.validUntil", e.target.value)}
                  aria-invalid={!!fieldErrors["agreementInfo.validUntil"]}
                />
              </div>
              {fieldErrors["agreementInfo.validUntil"] && (
                <div className="cm-agreement-error">{fieldErrors["agreementInfo.validUntil"]}</div>
              )}
            </div>
          </div>
        </div>

        <div className="cm-agreement-card cm-agreement-card--parties">
          <div className="cm-agreement-card-head">
            <h3>Contracting Parties</h3>
          </div>
          {CONTRACTING_PARTY_BLOCKS.map(({ key, heading }) => (
            <div key={key} className="cm-agreement-party-block">
              <h4 className="cm-agreement-party-title">{heading}</h4>
              <div className="cm-agreement-grid-4">
                <div className="cm-agreement-field">
                  <label>
                    Company Name<span className="cm-agreement-req">*</span>
                  </label>
                  <input
                    value={form.parties[key]?.companyName || ""}
                    onChange={(e) => setPath(`parties.${key}.companyName`, e.target.value)}
                    aria-invalid={!!fieldErrors[`parties.${key}.companyName`]}
                  />
                  {fieldErrors[`parties.${key}.companyName`] && (
                    <div className="cm-agreement-error">{fieldErrors[`parties.${key}.companyName`]}</div>
                  )}
                </div>
                <div className="cm-agreement-field">
                  <label>
                    Authorized Representative<span className="cm-agreement-req">*</span>
                  </label>
                  <input
                    placeholder="Name"
                    value={form.parties[key]?.authorizedRepresentative || ""}
                    onChange={(e) => setPath(`parties.${key}.authorizedRepresentative`, e.target.value)}
                    aria-invalid={!!fieldErrors[`parties.${key}.authorizedRepresentative`]}
                  />
                  {fieldErrors[`parties.${key}.authorizedRepresentative`] && (
                    <div className="cm-agreement-error">{fieldErrors[`parties.${key}.authorizedRepresentative`]}</div>
                  )}
                </div>
                <div className="cm-agreement-field">
                  <label>
                    Designation<span className="cm-agreement-req">*</span>
                  </label>
                  <input
                    placeholder="e.g., CEO, Director"
                    value={form.parties[key]?.designation || ""}
                    onChange={(e) => setPath(`parties.${key}.designation`, e.target.value)}
                    aria-invalid={!!fieldErrors[`parties.${key}.designation`]}
                  />
                  {fieldErrors[`parties.${key}.designation`] && (
                    <div className="cm-agreement-error">{fieldErrors[`parties.${key}.designation`]}</div>
                  )}
                </div>
                <div className="cm-agreement-field">
                  <label>
                    Email<span className="cm-agreement-req">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="email@company.com"
                    autoComplete="email"
                    value={form.parties[key]?.email || ""}
                    onChange={(e) => setPath(`parties.${key}.email`, e.target.value)}
                    aria-invalid={!!fieldErrors[`parties.${key}.email`]}
                  />
                  {fieldErrors[`parties.${key}.email`] && (
                    <div className="cm-agreement-error">{fieldErrors[`parties.${key}.email`]}</div>
                  )}
                </div>
              </div>
              <div className="cm-agreement-party-row-2">
                <div className="cm-agreement-field">
                  <label>
                    Registered Address<span className="cm-agreement-req">*</span>
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Enter complete address"
                    value={form.parties[key]?.registeredAddress || ""}
                    onChange={(e) => setPath(`parties.${key}.registeredAddress`, e.target.value)}
                    aria-invalid={!!fieldErrors[`parties.${key}.registeredAddress`]}
                  />
                  {fieldErrors[`parties.${key}.registeredAddress`] && (
                    <div className="cm-agreement-error">{fieldErrors[`parties.${key}.registeredAddress`]}</div>
                  )}
                </div>
                <div className="cm-agreement-field">
                  <label>
                    Contact Number<span className="cm-agreement-req">*</span>
                  </label>
                  <input
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    autoComplete="tel"
                    value={form.parties[key]?.contactNumber || ""}
                    onChange={(e) => setPath(`parties.${key}.contactNumber`, e.target.value)}
                    aria-invalid={!!fieldErrors[`parties.${key}.contactNumber`]}
                  />
                  {fieldErrors[`parties.${key}.contactNumber`] && (
                    <div className="cm-agreement-error">{fieldErrors[`parties.${key}.contactNumber`]}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="cm-agreement-card cm-agreement-card--section">
          <div className="cm-agreement-card-head">
            <h3>Commercial Terms</h3>
          </div>
          <div className="cm-agreement-grid-4">
            <div className="cm-agreement-field">
              <label>
                Total Contract Value<span className="cm-agreement-req">*</span>
              </label>
              <input
                inputMode="decimal"
                placeholder="$45,000"
                value={form.pricing.totalAmount}
                onChange={(e) => setPath("pricing.totalAmount", e.target.value)}
                aria-invalid={!!fieldErrors["pricing.totalAmount"]}
              />
              {fieldErrors["pricing.totalAmount"] && (
                <div className="cm-agreement-error">{fieldErrors["pricing.totalAmount"]}</div>
              )}
            </div>
            <div className="cm-agreement-field">
              <label>
                Currency<span className="cm-agreement-req">*</span>
              </label>
              <select
                value={form.agreementInfo.currency ?? ""}
                onChange={(e) => {
                  setPath("agreementInfo.currency", e.target.value);
                  if (fieldErrors["agreementInfo.currency"]) {
                    setFieldErrors((prev) => ({ ...prev, "agreementInfo.currency": undefined }));
                  }
                }}
                aria-invalid={!!fieldErrors["agreementInfo.currency"]}
              >
                {currencySelectOptions.map((o, i) => (
                  <option key={`cur-${i}-${String(o.value)}`} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {fieldErrors["agreementInfo.currency"] && (
                <div className="cm-agreement-error">{fieldErrors["agreementInfo.currency"]}</div>
              )}
            </div>
            <div className="cm-agreement-field">
              <label>
                Payment Terms<span className="cm-agreement-req">*</span>
              </label>
              <select
                value={form.commercial.paymentTerms ?? ""}
                onChange={(e) => {
                  setPath("commercial.paymentTerms", coerceMasterSelectValue(e.target.value));
                  if (fieldErrors["commercial.paymentTerms"]) {
                    setFieldErrors((prev) => ({ ...prev, "commercial.paymentTerms": undefined }));
                  }
                }}
                aria-invalid={!!fieldErrors["commercial.paymentTerms"]}
              >
                {paymentTermsSelectOptions.map((o, i) => (
                  <option key={`pt-${i}-${String(o.value)}`} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {fieldErrors["commercial.paymentTerms"] && (
                <div className="cm-agreement-error">{fieldErrors["commercial.paymentTerms"]}</div>
              )}
            </div>
            <div className="cm-agreement-field">
              <label>
                Incoterms<span className="cm-agreement-req">*</span>
              </label>
              <select
                value={form.commercial.incoterms ?? ""}
                onChange={(e) => {
                  setPath("commercial.incoterms", coerceMasterSelectValue(e.target.value));
                  if (fieldErrors["commercial.incoterms"]) {
                    setFieldErrors((prev) => ({ ...prev, "commercial.incoterms": undefined }));
                  }
                }}
                aria-invalid={!!fieldErrors["commercial.incoterms"]}
              >
                {incotermsSelectOptions.map((o, i) => (
                  <option key={`inc-${i}-${String(o.value)}`} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {fieldErrors["commercial.incoterms"] && (
                <div className="cm-agreement-error">{fieldErrors["commercial.incoterms"]}</div>
              )}
            </div>
          </div>
          <div className="cm-agreement-grid-3 cm-agreement-commercial-row-2">
            <div className="cm-agreement-field">
              <label>
                Late Delivery Penalty<span className="cm-agreement-req">*</span>
              </label>
              <input
                placeholder="e.g., 1% per week"
                value={form.commercial.lateDeliveryPenalty}
                onChange={(e) => setPath("commercial.lateDeliveryPenalty", e.target.value)}
                aria-invalid={!!fieldErrors["commercial.lateDeliveryPenalty"]}
              />
              {fieldErrors["commercial.lateDeliveryPenalty"] && (
                <div className="cm-agreement-error">{fieldErrors["commercial.lateDeliveryPenalty"]}</div>
              )}
            </div>
            <div className="cm-agreement-field">
              <label>
                Warranty Period<span className="cm-agreement-req">*</span>
              </label>
              <input
                placeholder="e.g., 12 months"
                value={form.commercial.warrantyPeriod}
                onChange={(e) => setPath("commercial.warrantyPeriod", e.target.value)}
                aria-invalid={!!fieldErrors["commercial.warrantyPeriod"]}
              />
              {fieldErrors["commercial.warrantyPeriod"] && (
                <div className="cm-agreement-error">{fieldErrors["commercial.warrantyPeriod"]}</div>
              )}
            </div>
            <div className="cm-agreement-field">
              <label>Security Deposit</label>
              <input
                placeholder="Amount or %"
                value={form.commercial.securityDeposit}
                onChange={(e) => setPath("commercial.securityDeposit", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="cm-agreement-card cm-agreement-card--section">
          <div className="cm-agreement-card-head cm-agreement-card-head--actions">
            <h3>Payment Milestones</h3>
            <button type="button" className="cm-agreement-btn-add" onClick={addMilestone}>
              <FaPlus aria-hidden />
              Add Milestone
            </button>
          </div>
          {(form.paymentMilestones || []).map((m, i) => (
            <div key={m.id} className="cm-agreement-milestone">
              <div className="cm-agreement-milestone-grid">
                <div className="cm-agreement-field">
                  <label>
                    Milestone Name<span className="cm-agreement-req">*</span>
                  </label>
                  <input
                    placeholder="e.g., Advance Payment"
                    value={m.name}
                    onChange={(e) => updateMilestone(i, "name", e.target.value)}
                    aria-invalid={!!fieldErrors[`paymentMilestones.${i}.name`]}
                  />
                  {fieldErrors[`paymentMilestones.${i}.name`] && (
                    <div className="cm-agreement-error">{fieldErrors[`paymentMilestones.${i}.name`]}</div>
                  )}
                </div>
                <div className="cm-agreement-field">
                  <label>
                    Deliverable<span className="cm-agreement-req">*</span>
                  </label>
                  <input
                    placeholder="e.g., Purchase Order"
                    value={m.deliverable}
                    onChange={(e) => updateMilestone(i, "deliverable", e.target.value)}
                    aria-invalid={!!fieldErrors[`paymentMilestones.${i}.deliverable`]}
                  />
                  {fieldErrors[`paymentMilestones.${i}.deliverable`] && (
                    <div className="cm-agreement-error">{fieldErrors[`paymentMilestones.${i}.deliverable`]}</div>
                  )}
                </div>
                <div className="cm-agreement-field">
                  <label>
                    Due Date<span className="cm-agreement-req">*</span>
                  </label>
                  <div className="cm-agreement-date-field">
                    <FaCalendarAlt className="cm-agreement-date-icon" aria-hidden />
                    <input
                      type="date"
                      className="cm-agreement-date-input"
                      value={m.dueDate}
                      onChange={(e) => updateMilestone(i, "dueDate", e.target.value)}
                      aria-invalid={!!fieldErrors[`paymentMilestones.${i}.dueDate`]}
                    />
                  </div>
                  {fieldErrors[`paymentMilestones.${i}.dueDate`] && (
                    <div className="cm-agreement-error">{fieldErrors[`paymentMilestones.${i}.dueDate`]}</div>
                  )}
                </div>
                <div className="cm-agreement-field cm-agreement-field--pct">
                  <label>
                    Payment %<span className="cm-agreement-req">*</span>
                  </label>
                  <input
                    inputMode="decimal"
                    placeholder="50"
                    value={m.paymentPercent}
                    onChange={(e) => updateMilestone(i, "paymentPercent", e.target.value)}
                    aria-invalid={!!fieldErrors[`paymentMilestones.${i}.paymentPercent`]}
                  />
                  {fieldErrors[`paymentMilestones.${i}.paymentPercent`] && (
                    <div className="cm-agreement-error">{fieldErrors[`paymentMilestones.${i}.paymentPercent`]}</div>
                  )}
                </div>
              </div>
              {(form.paymentMilestones || []).length > 1 && (
                <button
                  type="button"
                  className="cm-agreement-milestone-remove"
                  onClick={() => removeMilestone(i)}
                  aria-label="Remove milestone"
                >
                  <FaTimes aria-hidden />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="cm-agreement-card cm-agreement-card--section">
          <div className="cm-agreement-card-head">
            <h3>Agreement Clauses</h3>
          </div>
          <div className="cm-agreement-clause-stack">
            <div className="cm-agreement-field">
              <label>
                1. Scope of Work<span className="cm-agreement-req">*</span>
              </label>
              <textarea
                rows={4}
                placeholder="Enter scope of work details..."
                value={form.agreementClauses.scopeOfWork}
                onChange={(e) => setPath("agreementClauses.scopeOfWork", e.target.value)}
                aria-invalid={!!fieldErrors["agreementClauses.scopeOfWork"]}
              />
              {fieldErrors["agreementClauses.scopeOfWork"] && (
                <div className="cm-agreement-error">{fieldErrors["agreementClauses.scopeOfWork"]}</div>
              )}
            </div>
            <div className="cm-agreement-field">
              <label>
                2. Quality Standards<span className="cm-agreement-req">*</span>
              </label>
              <textarea
                rows={4}
                placeholder="Enter quality standards details..."
                value={form.agreementClauses.qualityStandards}
                onChange={(e) => setPath("agreementClauses.qualityStandards", e.target.value)}
                aria-invalid={!!fieldErrors["agreementClauses.qualityStandards"]}
              />
              {fieldErrors["agreementClauses.qualityStandards"] && (
                <div className="cm-agreement-error">{fieldErrors["agreementClauses.qualityStandards"]}</div>
              )}
            </div>
            <div className="cm-agreement-field">
              <label>
                3. Delivery Terms<span className="cm-agreement-req">*</span>
              </label>
              <textarea
                rows={4}
                placeholder="Enter delivery terms details..."
                value={form.agreementClauses.deliveryTerms}
                onChange={(e) => setPath("agreementClauses.deliveryTerms", e.target.value)}
                aria-invalid={!!fieldErrors["agreementClauses.deliveryTerms"]}
              />
              {fieldErrors["agreementClauses.deliveryTerms"] && (
                <div className="cm-agreement-error">{fieldErrors["agreementClauses.deliveryTerms"]}</div>
              )}
            </div>
          </div>
        </div>

        <div className="cm-agreement-card cm-agreement-card--section">
          <div className="cm-agreement-card-head cm-agreement-card-head--actions">
            <h3>Additional Custom Clauses</h3>
            <button type="button" className="cm-agreement-btn-add" onClick={addCustomClause}>
              <FaPlus aria-hidden />
              Add Clause
            </button>
          </div>
          {(form.customClauses || []).map((c, i) => (
            <div key={c.id} className="cm-agreement-custom-clause">
              <button
                type="button"
                className="cm-agreement-custom-clause-remove"
                onClick={() => removeCustomClause(i)}
                aria-label="Remove clause"
              >
                <FaTimes aria-hidden />
              </button>
              <div className="cm-agreement-field">
                <input
                  placeholder="Clause Title"
                  value={c.title}
                  onChange={(e) => updateCustomClause(i, "title", e.target.value)}
                  aria-invalid={!!fieldErrors[`customClauses.${i}.title`]}
                />
                {fieldErrors[`customClauses.${i}.title`] && (
                  <div className="cm-agreement-error">{fieldErrors[`customClauses.${i}.title`]}</div>
                )}
              </div>
              <div className="cm-agreement-field">
                <textarea
                  rows={4}
                  placeholder="Clause content..."
                  value={c.content}
                  onChange={(e) => updateCustomClause(i, "content", e.target.value)}
                  aria-invalid={!!fieldErrors[`customClauses.${i}.content`]}
                />
                {fieldErrors[`customClauses.${i}.content`] && (
                  <div className="cm-agreement-error">{fieldErrors[`customClauses.${i}.content`]}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="cm-agreement-card cm-agreement-card--section">
          <div className="cm-agreement-card-head">
            <h3>Termination & Dispute Resolution</h3>
          </div>
          <div className="cm-agreement-grid-2">
            <div className="cm-agreement-field">
              <label>
                Termination Notice Period<span className="cm-agreement-req">*</span>
              </label>
              <select
                value={form.legal.terminationNoticePeriod}
                onChange={(e) => {
                  setPath("legal.terminationNoticePeriod", e.target.value);
                  if (fieldErrors["legal.terminationNoticePeriod"]) {
                    setFieldErrors((prev) => ({ ...prev, "legal.terminationNoticePeriod": undefined }));
                  }
                }}
                aria-invalid={!!fieldErrors["legal.terminationNoticePeriod"]}
              >
                {TERMINATION_NOTICE_OPTIONS.map((o) => (
                  <option key={o.value || "tn"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {fieldErrors["legal.terminationNoticePeriod"] && (
                <div className="cm-agreement-error">{fieldErrors["legal.terminationNoticePeriod"]}</div>
              )}
            </div>
            <div className="cm-agreement-field">
              <label>
                Dispute Resolution Method<span className="cm-agreement-req">*</span>
              </label>
              <select
                value={form.legal.disputeResolution}
                onChange={(e) => {
                  setPath("legal.disputeResolution", e.target.value);
                  if (fieldErrors["legal.disputeResolution"]) {
                    setFieldErrors((prev) => ({ ...prev, "legal.disputeResolution": undefined }));
                  }
                }}
                aria-invalid={!!fieldErrors["legal.disputeResolution"]}
              >
                {DISPUTE_RESOLUTION_OPTIONS.map((o) => (
                  <option key={o.value || "dr"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {fieldErrors["legal.disputeResolution"] && (
                <div className="cm-agreement-error">{fieldErrors["legal.disputeResolution"]}</div>
              )}
            </div>
          </div>
          <div className="cm-agreement-field cm-agreement-field--mt">
            <label>Termination Conditions</label>
            <textarea
              rows={4}
              placeholder="Specify conditions under which the agreement can be terminated..."
              value={form.legal.terminationConditions}
              onChange={(e) => setPath("legal.terminationConditions", e.target.value)}
            />
          </div>
        </div>

        <div className="cm-agreement-card cm-agreement-card--section cm-agreement-card--confidentiality">
          <div className="cm-agreement-card-head">
            <h3>Confidentiality & Intellectual Property</h3>
          </div>
          <div className="cm-agreement-field cm-agreement-field--select-like cm-agreement-field--ip">
            <label className="cm-agreement-field-label-bold cm-agreement-field-label-block">
              IP Ownership<span className="cm-agreement-req">*</span>
            </label>
            <select
              className="cm-agreement-select-native"
              value={form.confidentiality.ipOwnership}
              onChange={(e) => {
                setPath("confidentiality.ipOwnership", e.target.value);
                if (fieldErrors["confidentiality.ipOwnership"]) {
                  setFieldErrors((prev) => ({ ...prev, "confidentiality.ipOwnership": undefined }));
                }
              }}
              aria-invalid={!!fieldErrors["confidentiality.ipOwnership"]}
            >
              {IP_OWNERSHIP_OPTIONS.map((o) => (
                <option key={o.value || "ipo"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {fieldErrors["confidentiality.ipOwnership"] && (
              <div className="cm-agreement-error">{fieldErrors["confidentiality.ipOwnership"]}</div>
            )}
          </div>
          <div className="cm-agreement-checkbox-stack" role="group" aria-label="Confidentiality options">
            <label className="cm-agreement-check">
              <input
                type="checkbox"
                checked={!!form.confidentiality.ndaApplies}
                onChange={(e) => setPath("confidentiality.ndaApplies", e.target.checked)}
              />
              <span>Standard confidentiality and NDA terms apply to all disclosed information</span>
            </label>
            <label className="cm-agreement-check">
              <input
                type="checkbox"
                checked={!!form.confidentiality.technicalDataConfidential}
                onChange={(e) => setPath("confidentiality.technicalDataConfidential", e.target.checked)}
              />
              <span>Technical specifications, drawings, and process data are confidential</span>
            </label>
            <label className="cm-agreement-check">
              <input
                type="checkbox"
                checked={!!form.confidentiality.marketingUseRestricted}
                onChange={(e) => setPath("confidentiality.marketingUseRestricted", e.target.checked)}
              />
              <span>Marketing or public use of project information requires prior written consent</span>
            </label>
          </div>
        </div>

        <div className="cm-agreement-card cm-agreement-card--section cm-agreement-card--annexures">
          <div className="cm-agreement-section-head cm-agreement-section-head--annexures">
            <span className="cm-agreement-section-icon cm-agreement-section-icon--upload" aria-hidden>
              <FaCloudUploadAlt />
            </span>
            <h3 className="cm-agreement-section-title">Annexures & Attachments</h3>
          </div>
          <div className="cm-agreement-annexures-zone">
            <FaCloudUploadAlt className="cm-agreement-annexures-icon" aria-hidden />
            <strong>Upload supporting documents</strong>
            <span>Technical specifications, drawings, quotations, etc.</span>
            <input
              ref={annexureFileInputRef}
              type="file"
              className="cm-agreement-annexures-input"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setAttachments(files);
              }}
            />
            <button
              type="button"
              className="cm-agreement-btn-browse"
              onClick={() => annexureFileInputRef.current?.click()}
            >
              <FaUpload aria-hidden />
              Browse Files
            </button>
          </div>
          {attachments.length > 0 && (
            <div className="cm-agreement-annexures-list">
              <strong>Selected files</strong>
              <ul>
                {attachments.map((f) => (
                  <li key={f.name}>
                    {f.name} ({Math.round((f.size || 0) / 1024)} KB)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="cm-agreement-footer cm-agreement-footer--figma">
        <div className="cm-agreement-footer-inner cm-agreement-footer-inner--split">
          <button
            type="button"
            className="cm-agreement-btn-preview"
            onClick={previewAgreement}
            disabled={saving}
          >
            <FaEye aria-hidden />
            Preview Agreement
          </button>
          <div className="cm-agreement-footer-actions cm-agreement-footer-actions--figma">
            {!isAgreementLocked && (
              <button type="button" className="cm-agreement-btn-footer-secondary" onClick={() => save("draft")} disabled={saving}>
                <FaSave aria-hidden />
                Save as Draft
              </button>
            )}
            <button type="button" className="cm-agreement-btn-footer-secondary" onClick={exportAgreementPdf} disabled={saving}>
              <FaDownload aria-hidden />
              Export as PDF
            </button>
            {!isAgreementLocked && (
              <button type="button" className="cm-agreement-btn-send-signature" onClick={() => save("submitted")} disabled={saving}>
                <FaPaperPlane aria-hidden />
                Send for Signature
              </button>
            )}
          </div>
        </div>
      </div>

      {previewOpen && (
        <div
          className="cm-agreement-preview-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cm-agreement-preview-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePreview();
          }}
        >
          <div className="cm-agreement-preview-modal">
            <div className="cm-agreement-preview-toolbar">
              <h2 id="cm-agreement-preview-title" className="cm-agreement-preview-heading">
                Agreement preview
              </h2>
              <div className="cm-agreement-preview-toolbar-actions">
                <button type="button" className="cm-agreement-btn-footer-secondary" onClick={printPreview}>
                  Print
                </button>
                <button type="button" className="cm-agreement-btn-preview" onClick={closePreview}>
                  <FaTimes aria-hidden /> Close
                </button>
              </div>
            </div>
            <div className="cm-agreement-preview-content" id="cm-agreement-preview-print">
              <header className="cm-agreement-preview-doc-head">
                <h1 className="cm-agreement-preview-doc-title">{dash(form.agreementInfo.title)}</h1>
                <p className="cm-agreement-preview-doc-meta">
                  {dash(form.agreementInfo.agreementNumber)} · RFQ {summary.rfqNo} · Quotation {summary.qtNo}
                </p>
              </header>

              <section className="cm-agreement-preview-section">
                <h3 className="cm-agreement-preview-h3">Agreement basic information</h3>
                <dl className="cm-agreement-preview-dl">
                  <div>
                    <dt>Agreement type</dt>
                    <dd>{optLabel(form.agreementInfo.agreementType, AGREEMENT_TYPE_OPTIONS)}</dd>
                  </div>
                  <div>
                    <dt>Governing law</dt>
                    <dd>{optLabel(form.agreementInfo.governingLaw, GOVERNING_LAW_OPTIONS)}</dd>
                  </div>
                  <div>
                    <dt>Start date</dt>
                    <dd>{dash(form.agreementInfo.effectiveDate)}</dd>
                  </div>
                  <div>
                    <dt>End date</dt>
                    <dd>{dash(form.agreementInfo.validUntil)}</dd>
                  </div>
                </dl>
              </section>

              <section className="cm-agreement-preview-section">
                <h3 className="cm-agreement-preview-h3">Contracting parties</h3>
                {CONTRACTING_PARTY_BLOCKS.map(({ key, heading }) => (
                  <div key={key} className="cm-agreement-preview-party">
                    <h4 className="cm-agreement-preview-h4">{heading}</h4>
                    <dl className="cm-agreement-preview-dl cm-agreement-preview-dl--dense">
                      <div>
                        <dt>Company</dt>
                        <dd>{dash(form.parties[key]?.companyName)}</dd>
                      </div>
                      <div>
                        <dt>Authorized representative</dt>
                        <dd>{dash(form.parties[key]?.authorizedRepresentative)}</dd>
                      </div>
                      <div>
                        <dt>Designation</dt>
                        <dd>{dash(form.parties[key]?.designation)}</dd>
                      </div>
                      <div>
                        <dt>Email</dt>
                        <dd>{dash(form.parties[key]?.email)}</dd>
                      </div>
                      <div className="cm-agreement-preview-span-2">
                        <dt>Registered address</dt>
                        <dd>{dash(form.parties[key]?.registeredAddress)}</dd>
                      </div>
                      <div>
                        <dt>Contact</dt>
                        <dd>{dash(form.parties[key]?.contactNumber)}</dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </section>

              <section className="cm-agreement-preview-section">
                <h3 className="cm-agreement-preview-h3">Commercial terms</h3>
                <dl className="cm-agreement-preview-dl">
                  <div>
                    <dt>Total contract value</dt>
                    <dd>
                      {form.pricing?.totalAmount !== "" && form.pricing?.totalAmount != null
                        ? formatCurrency(form.pricing.totalAmount, String(form.agreementInfo.currency || "USD"))
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>Currency</dt>
                    <dd>{optLabel(form.agreementInfo.currency, currencySelectOptions)}</dd>
                  </div>
                  <div>
                    <dt>Payment terms</dt>
                    <dd>{optLabel(form.commercial.paymentTerms, paymentTermsSelectOptions)}</dd>
                  </div>
                  <div>
                    <dt>Incoterms</dt>
                    <dd>{optLabel(form.commercial.incoterms, incotermsSelectOptions)}</dd>
                  </div>
                  <div>
                    <dt>Late delivery penalty</dt>
                    <dd>{dash(form.commercial.lateDeliveryPenalty)}</dd>
                  </div>
                  <div>
                    <dt>Warranty period</dt>
                    <dd>{dash(form.commercial.warrantyPeriod)}</dd>
                  </div>
                  <div>
                    <dt>Security deposit</dt>
                    <dd>{dash(form.commercial.securityDeposit)}</dd>
                  </div>
                </dl>
              </section>

              <section className="cm-agreement-preview-section">
                <h3 className="cm-agreement-preview-h3">Payment milestones</h3>
                {(form.paymentMilestones || []).length === 0 ? (
                  <p className="cm-agreement-preview-muted">No milestones.</p>
                ) : (
                  <table className="cm-agreement-preview-table">
                    <thead>
                      <tr>
                        <th>Milestone</th>
                        <th>Deliverable</th>
                        <th>Due date</th>
                        <th>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.paymentMilestones || []).map((m) => (
                        <tr key={m.id}>
                          <td>{dash(m.name)}</td>
                          <td>{dash(m.deliverable)}</td>
                          <td>{dash(m.dueDate)}</td>
                          <td>{dash(m.paymentPercent)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="cm-agreement-preview-section">
                <h3 className="cm-agreement-preview-h3">Agreement clauses</h3>
                <div className="cm-agreement-preview-block">
                  <strong>1. Scope of work</strong>
                  <p>{dash(form.agreementClauses?.scopeOfWork)}</p>
                </div>
                <div className="cm-agreement-preview-block">
                  <strong>2. Quality standards</strong>
                  <p>{dash(form.agreementClauses?.qualityStandards)}</p>
                </div>
                <div className="cm-agreement-preview-block">
                  <strong>3. Delivery terms</strong>
                  <p>{dash(form.agreementClauses?.deliveryTerms)}</p>
                </div>
              </section>

              {(form.customClauses || []).length > 0 && (
                <section className="cm-agreement-preview-section">
                  <h3 className="cm-agreement-preview-h3">Additional custom clauses</h3>
                  {(form.customClauses || []).map((c) => (
                    <div key={c.id} className="cm-agreement-preview-block">
                      <strong>{dash(c.title)}</strong>
                      <p>{dash(c.content)}</p>
                    </div>
                  ))}
                </section>
              )}

              <section className="cm-agreement-preview-section">
                <h3 className="cm-agreement-preview-h3">Termination & dispute resolution</h3>
                <dl className="cm-agreement-preview-dl">
                  <div>
                    <dt>Termination notice period</dt>
                    <dd>{optLabel(form.legal.terminationNoticePeriod, TERMINATION_NOTICE_OPTIONS)}</dd>
                  </div>
                  <div>
                    <dt>Dispute resolution</dt>
                    <dd>{optLabel(form.legal.disputeResolution, DISPUTE_RESOLUTION_OPTIONS)}</dd>
                  </div>
                  <div className="cm-agreement-preview-span-2">
                    <dt>Termination conditions</dt>
                    <dd>{dash(form.legal.terminationConditions)}</dd>
                  </div>
                </dl>
              </section>

              <section className="cm-agreement-preview-section">
                <h3 className="cm-agreement-preview-h3">Confidentiality & intellectual property</h3>
                <dl className="cm-agreement-preview-dl">
                  <div className="cm-agreement-preview-span-2">
                    <dt>IP ownership</dt>
                    <dd>{optLabel(form.confidentiality?.ipOwnership, IP_OWNERSHIP_OPTIONS)}</dd>
                  </div>
                </dl>
                <ul className="cm-agreement-preview-checks">
                  <li>{form.confidentiality?.ndaApplies ? "☑" : "☐"} Standard confidentiality and NDA terms apply</li>
                  <li>
                    {form.confidentiality?.technicalDataConfidential ? "☑" : "☐"} Technical specifications and process data are confidential
                  </li>
                  <li>
                    {form.confidentiality?.marketingUseRestricted ? "☑" : "☐"} Marketing use requires prior written consent
                  </li>
                </ul>
              </section>

              <section className="cm-agreement-preview-section">
                <h3 className="cm-agreement-preview-h3">Annexures & attachments</h3>
                {attachments.length === 0 ? (
                  <p className="cm-agreement-preview-muted">No files selected.</p>
                ) : (
                  <ul className="cm-agreement-preview-files">
                    {attachments.map((f) => (
                      <li key={f.name}>
                        {f.name} ({Math.round((f.size || 0) / 1024)} KB)
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

